import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { type ColumnDef } from '@tanstack/react-table'
import { Database, MonitorSmartphone, Network, RefreshCw } from 'lucide-react'
import { Link } from 'react-router-dom'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from '@/components/ui'
import { cn } from '@/lib/utils.ts'

// ─── Types ───────────────────────────────────────────────────────────────────

interface CmdbAsset {
  id: string
  name: string
  hostname: string | null
  ip: string | null
  customer: { id: string; name: string } | null
  site: string | null
  sources: {
    ninjaone?: { deviceId: number; online: boolean; lastContact: number | null }
    unifi?: { mac: string; model: string | null; type: string; status: 'online' | 'offline'; ip: string | null }
  }
  online: boolean | null
  type: 'endpoint' | 'network_device'
}

// ─── Source badge ─────────────────────────────────────────────────────────────

function SourceBadge({ label, active, color }: { label: string; active: boolean; color: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium',
        active ? `${color} text-white` : 'bg-gray-100 text-gray-400',
      )}
    >
      {label}
    </span>
  )
}

// ─── Columns ─────────────────────────────────────────────────────────────────

const columns: ColumnDef<CmdbAsset>[] = [
  {
    id: 'online',
    header: '',
    size: 12,
    cell: ({ row }) => (
      <span
        className={cn(
          'block h-2 w-2 rounded-full',
          row.original.online === true && 'bg-green-500',
          row.original.online === false && 'bg-red-400',
          row.original.online === null && 'bg-gray-300',
        )}
      />
    ),
  },
  {
    accessorKey: 'name',
    header: 'Asset',
    cell: ({ row }) => (
      <div>
        <span className="font-medium text-sm">{row.original.name}</span>
        {row.original.hostname && row.original.hostname !== row.original.name.toLowerCase() && (
          <p className="text-xs text-gray-400 font-mono">{row.original.hostname}</p>
        )}
      </div>
    ),
  },
  {
    id: 'customer',
    header: 'Kunde / Standort',
    accessorFn: (row) => row.customer?.name ?? row.site ?? '',
    cell: ({ row }) => {
      if (row.original.customer) {
        return (
          <Link to={`/kunden/${row.original.customer.id}`} className="text-brand-600 hover:underline text-sm">
            {row.original.customer.name}
          </Link>
        )
      }
      return <span className="text-sm text-gray-600">{row.original.site ?? '–'}</span>
    },
  },
  {
    accessorKey: 'ip',
    header: 'IP',
    size: 130,
    cell: ({ row }) => (
      <span className="font-mono text-xs text-gray-600">{row.original.ip ?? '–'}</span>
    ),
  },
  {
    id: 'type',
    header: 'Typ',
    size: 120,
    accessorFn: (row) => row.type,
    cell: ({ row }) => (
      <span className="text-xs text-gray-500">
        {row.original.type === 'endpoint' ? 'Endgerät' : 'Netzwerkgerät'}
      </span>
    ),
  },
  {
    id: 'sources',
    header: 'Systeme',
    size: 160,
    cell: ({ row }) => (
      <div className="flex gap-1">
        <SourceBadge label="NinjaOne" active={!!row.original.sources.ninjaone} color="bg-green-600" />
        <SourceBadge label="UniFi" active={!!row.original.sources.unifi} color="bg-blue-600" />
      </div>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    size: 90,
    cell: ({ row }) => {
      if (row.original.online === null) return <span className="text-gray-300 text-xs">–</span>
      return <StatusBadge status={row.original.online ? 'online' : 'offline'} size="sm" />
    },
  },
]

// ─── Page ─────────────────────────────────────────────────────────────────────

export function CmdbPage() {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery<CmdbAsset[]>({
    queryKey: ['cmdb', 'assets'],
    queryFn: () => apiClient.get('/cmdb/assets').then((r) => r.data),
    staleTime: 3 * 60 * 1000,
    retry: false,
  })

  const stats = useMemo(() => {
    if (!data) return null
    const endpoints = data.filter((a) => a.type === 'endpoint').length
    const networkDevices = data.filter((a) => a.type === 'network_device').length
    const inNinja = data.filter((a) => a.sources.ninjaone).length
    const inUnifi = data.filter((a) => a.sources.unifi).length
    const inBoth = data.filter((a) => a.sources.ninjaone && a.sources.unifi).length
    const online = data.filter((a) => a.online === true).length
    return { total: data.length, endpoints, networkDevices, inNinja, inUnifi, inBoth, online }
  }, [data])

  return (
    <div className="space-y-6">
      <PageHeader
        title="CMDB – Asset-Übersicht"
        description="Alle Geräte und Systeme aus allen Quellen in einer Übersicht"
        actions={
          <Button variant="secondary" size="sm" onClick={() => void refetch()} disabled={isFetching}>
            <RefreshCw className={cn('h-4 w-4 mr-1', isFetching && 'animate-spin')} />
            Aktualisieren
          </Button>
        }
      />

      {/* KPI strip */}
      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { icon: <Database className="h-4 w-4" />, label: 'Assets gesamt', value: stats.total },
            { icon: <MonitorSmartphone className="h-4 w-4" />, label: 'Endgeräte (NinjaOne)', value: stats.endpoints },
            { icon: <Network className="h-4 w-4" />, label: 'Netzwerkgeräte (UniFi)', value: stats.networkDevices },
            { icon: <span className="text-green-600 font-bold text-sm">●</span>, label: 'Online', value: `${stats.online} / ${stats.total}` },
          ].map((tile) => (
            <div key={tile.label} className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-card">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                {tile.icon}
                <span className="text-caption">{tile.label}</span>
              </div>
              <span className="text-h3 font-semibold text-gray-900">{tile.value}</span>
            </div>
          ))}
        </div>
      )}

      {isLoading && <LoadingState message="Assets werden aus allen Quellen geladen…" />}

      {isError && (
        <ErrorState
          title="Assets konnten nicht geladen werden"
          description={(error as Error).message}
        />
      )}

      {!isLoading && !isError && (
        <DataTable<CmdbAsset>
          columns={columns}
          data={data ?? []}
          getRowId={(a) => a.id}
          searchPlaceholder="Asset, Hostname oder Kunde suchen…"
          emptyState={
            <EmptyState
              icon={<Database />}
              title="Keine Assets gefunden"
              description="Noch keine Systeme konfiguriert. Richte NinjaOne und UniFi unter Einstellungen ein."
            />
          }
        />
      )}
    </div>
  )
}
